// TODO: add test suite

import { FormGroup } from '@angular/forms';
import { DataStorageService } from '../../services/data-storage.service';
import { Router, ActivatedRoute } from '@angular/router';
import { inject, OnInit, Directive } from '@angular/core';
import { formatStoredDate } from '../../utils/format-stored-date';

// Base class for invitation step forms
@Directive()
// This abstract class serves as a base for invitation step forms
export abstract class AcceptInvitationStepBase<T = any> implements OnInit {
  protected readonly dataStorageService = inject(DataStorageService);
  protected readonly router = inject(Router);
  protected readonly route = inject(ActivatedRoute);
  protected abstract readonly form: FormGroup;
  protected abstract readonly storageKey: string;
  protected abstract readonly nextUrl: string | null;
  protected abstract readonly previousUrl: string | null;

  public async ngOnInit(): Promise<void> {
    // Wait for the server-side draft to hydrate the cache before reading it,
    // so a refresh restores the saved values instead of showing an empty form.
    await this.dataStorageService.ready;
    this.loadFormData();

    // Subclass hook: runs AFTER saved data has been loaded into the form.
    // Put any logic that reads populated form values here (not in an ngOnInit
    // override), because hydration is asynchronous.
    this.afterFormLoaded();

    // On form changes, persist form data
    this.form.valueChanges.subscribe(() => {
      this.persistFormData();
    });
  }

  /** Overridable hook invoked once the form has been populated from the draft. */
  protected afterFormLoaded(): void {}

  protected loadFormData(): void {
    const saved = this.dataStorageService.getItem<T>(this.storageKey);
    if (saved) {
      this.form.patchValue(saved);
    }
  }

  protected persistFormData(): void {
    const val = this.form.value;

    // Change date formats to MM/dd/YYYY
    if (val.dateOfBirth != undefined) val.dateOfBirth = formatStoredDate(val.dateOfBirth);

    // Hardcode remove businessSsn from business structure form before saving
    if (this.storageKey === 'businessStructureForm') {
      delete val.businessSsn;
    }

    // Hardcode add personal email to about business form before saving
    // NOTE: this only works if:
    //  - personal details form is already filled and saved
    //  - about business form needs to be saved AFTER personal details form
    // If either of these conditions change, this logic will need to be updated
    if (this.storageKey === 'aboutBusinessForm') {
      const personalDetails = this.dataStorageService.getItem<any>('personalDetailsForm');
      val.businessEmail = personalDetails?.emailAddress;
    }

    // Hardcode to remove businessTaxID from about business form if sole proprietor
    if (this.storageKey === 'businessStructureForm' && val.businessStructure === 'SOLE_PROPRIETORSHIP') {
      delete val.businessTaxID;
    }

    this.dataStorageService.setItem(this.storageKey, val);
  }

  protected markAllFieldsAsTouchedIfInvalid(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
    }
  }

  public async continue(): Promise<void> {
    if (this.form.valid) {
      this.persistFormData();
      // Persist to the server draft immediately on step navigation so progress
      // is never lost between the debounced saves.
      await this.dataStorageService.flush();
      if (this.nextUrl) {
        await this.router.navigate([this.nextUrl], { relativeTo: this.route });
      }
    } else {
      this.markAllFieldsAsTouchedIfInvalid();
    }
  }

  public async goBack(): Promise<void> {
    if (this.previousUrl) {
      await this.router.navigate([this.previousUrl], { relativeTo: this.route });
    }
  }
}
